import React, { useEffect, useState } from 'react';
import {
  Typography,
  Box,
  Alert,
  Snackbar,
  Stack,
  TextField,
  Button,
  CircularProgress,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { clientApi } from '../../services/clientService';
import { useAuthStore } from '../../store/authStore';
import { PageHeader, SectionCard, FormSection, FieldGrid, LoadingState } from '../../components/ui';
import { tokens } from '../../theme';

const ConsentSwitch: React.FC<{ checked: boolean; onChange: (checked: boolean) => void; label: string; hint: string }> = ({
  checked, onChange, label, hint,
}) => (
  <FormControlLabel
    sx={{ alignItems: 'flex-start', m: 0 }}
    control={<Switch checked={checked} onChange={(e) => onChange(e.target.checked)} />}
    label={
      <Box sx={{ pt: 0.25 }}>
        <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600 }}>{label}</Typography>
        <Typography sx={{ fontSize: '0.875rem', color: tokens.muted }}>{hint}</Typography>
      </Box>
    }
  />
);

export const ClientProfilePage: React.FC = () => {
  const user = useAuthStore((s) => s.user);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    dateOfBirth: '',
    allergies: '',
    preferences: '',
    marketingConsent: false,
    smsConsent: false,
  });

  useEffect(() => {
    const load = async () => {
      try {
        const data = await clientApi.getProfile();
        setForm({
          firstName: data.firstName || '',
          lastName: data.lastName || '',
          phone: data.phone || '',
          dateOfBirth: data.dateOfBirth?.slice(0, 10) || '',
          allergies: data.allergies || '',
          preferences: data.preferences || '',
          marketingConsent: data.marketingConsent || false,
          smsConsent: data.smsConsent || false,
        });
      } catch {
        setError('Nie udało się załadować profilu. Odśwież stronę za chwilę.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await clientApi.updateProfile(form);
      setSuccess('Profil zapisany');
    } catch {
      setError('Nie udało się zapisać profilu. Sprawdź dane i spróbuj ponownie.');
    } finally {
      setSaving(false);
    }
  };

  const header = (
    <PageHeader
      title="Mój profil"
      description="Twoje dane kontaktowe i informacje, które pomagają salonowi dobrze przygotować wizytę."
    />
  );

  if (loading) {
    return (
      <Box>
        {header}
        <LoadingState variant="block" height={320} />
      </Box>
    );
  }

  return (
    <Box>
      {header}

      {error && <Alert severity="error" sx={{ mb: 2, maxWidth: 960 }} onClose={() => setError('')}>{error}</Alert>}

      <SectionCard sx={{ maxWidth: 960 }}>
        <FormSection first title="Dane osobowe" description="Salon zobaczy je przy Twojej rezerwacji.">
          <Stack gap={2}>
            <TextField
              label="E-mail"
              fullWidth
              value={user?.email || ''}
              disabled
              helperText="Adres, którym logujesz się do Visitio."
            />
            <FieldGrid columns={2}>
              <TextField label="Imię" fullWidth value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} />
              <TextField label="Nazwisko" fullWidth value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} />
              <TextField
                label="Telefon"
                type="tel"
                fullWidth
                placeholder="+48 123 456 789"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                helperText="Do kontaktu w sprawie Twoich wizyt."
              />
              <TextField
                label="Data urodzenia"
                type="date"
                fullWidth
                value={form.dateOfBirth}
                onChange={(e) => setForm({ ...form, dateOfBirth: e.target.value })}
                InputLabelProps={{ shrink: true }}
                helperText="Opcjonalnie."
              />
            </FieldGrid>
          </Stack>
        </FormSection>

        <FormSection title="Zdrowie i preferencje" description="Pomagają specjaliście bezpiecznie dobrać zabieg.">
          <Stack gap={2}>
            <TextField
              label="Alergie"
              fullWidth
              multiline
              minRows={2}
              placeholder="np. uczulenie na lateks, składniki kosmetyków"
              value={form.allergies}
              onChange={(e) => setForm({ ...form, allergies: e.target.value })}
              helperText="Informacja ważna dla bezpieczeństwa zabiegów."
            />
            <TextField
              label="Preferencje"
              fullWidth
              multiline
              minRows={2}
              value={form.preferences}
              onChange={(e) => setForm({ ...form, preferences: e.target.value })}
              helperText="Np. preferowana stylistka, ulubione zabiegi."
            />
          </Stack>
        </FormSection>

        <FormSection title="Zgody" description="Możesz je zmienić w każdej chwili.">
          <Stack gap={2}>
            <ConsentSwitch
              checked={form.marketingConsent}
              onChange={(checked) => setForm({ ...form, marketingConsent: checked })}
              label="Komunikacja marketingowa"
              hint="Informacje o ofertach i nowościach w salonach."
            />
            <ConsentSwitch
              checked={form.smsConsent}
              onChange={(checked) => setForm({ ...form, smsConsent: checked })}
              label="Powiadomienia SMS"
              hint="Wiadomości od salonów na Twój numer telefonu."
            />
          </Stack>
        </FormSection>

        <Stack direction="row" justifyContent="flex-end" sx={{ pt: 2.5, borderTop: `1px solid ${tokens.line}` }}>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            {saving ? 'Zapisywanie…' : 'Zapisz profil'}
          </Button>
        </Stack>
      </SectionCard>

      <Snackbar open={!!success} autoHideDuration={3000} onClose={() => setSuccess('')} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="success" variant="filled" onClose={() => setSuccess('')}>{success}</Alert>
      </Snackbar>
    </Box>
  );
};
